import React, { useState, useEffect, useRef } from 'react'
import { Routes, Route, useLocation, Link, Navigate, useNavigate } from 'react-router-dom'
import { Play, Pause, Square, RotateCcw, Database } from 'lucide-react'
import HeatmapCanvas from './components/HeatmapCanvas'
import Controls from './components/Controls'
import GraphPreview from './components/GraphPreview'
import PerformancePanel from './components/PerformancePanel'
import NoveltyPanel from './components/NoveltyPanel'
import APIv1Jobs from './components/APIv1Jobs'
import SwarmDashboard from './components/SwarmDashboard'
import Auth from './components/Auth'
import LandingPage from './components/LandingPage'
import NotFoundPage from './components/NotFoundPage'
import LanguageSwitcher from './components/LanguageSwitcher'
import { SimulationAPI } from './lib/api'
import { WebSocketClient } from './lib/ws'
import type { SimulationData, SimulationStatus, Metrics } from './lib/types'
import { useLocale } from './i18n/LocaleContext'
import logoImage from './assets/logo.jpg'
import headerImage from './assets/header.jpg'

const AUTH_TOKEN_KEY = 'live2_auth_token'
const AUTH_API_KEY_KEY = 'live2_api_key'

const App: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useLocale()
  const pathname = location.pathname.replace(/\/+$/, '') || '/'
  const [simulationId, setSimulationId] = useState<string | null>(null)
  const [status, setStatus] = useState<SimulationStatus | null>(null)
  const [simulationData, setSimulationData] = useState<SimulationData | null>(null)
  const [metricsSnapshot, setMetricsSnapshot] = useState<Metrics | null>(null)
  const [performanceData, setPerformanceData] = useState<any>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [selectedSubstance, setSelectedSubstance] = useState<string | null>(null)
  const [mode, setMode] = useState<'preset_prebiotic' | 'open_chemistry'>('open_chemistry')
  
  // Determine view from URL
  const view = pathname === '/swarm' ? 'swarm' : pathname === '/api-v1' ? 'api-v1' : pathname === '/simulation' ? 'simulation' : 'public'
  const isSimulationView = view === 'simulation'
  const isAppView = view === 'simulation' || view === 'api-v1' || view === 'swarm'
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return Boolean(localStorage.getItem(AUTH_TOKEN_KEY))
  })
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login')
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [availableSpecies, setAvailableSpecies] = useState<string[]>([])
  const [runtimeStartMs, setRuntimeStartMs] = useState<number | null>(null)
  const [runtimeAccumulatedMs, setRuntimeAccumulatedMs] = useState<number>(0)
  const [runtimeNowMs, setRuntimeNowMs] = useState<number>(Date.now())
  
  const api = useRef(new SimulationAPI()).current
  const wsClient = useRef(new WebSocketClient()).current
  
  // FIX: Update runtime clock every second
  useEffect(() => {
    const interval = setInterval(() => {
      setRuntimeNowMs(Date.now())
    }, 1000) // Update every second
    
    return () => clearInterval(interval)
  }, [])
  const wsHandlersRef = useRef<Record<string, Function>>({})
  const modeInitializedRef = useRef(false)
  const simulationIdRef = useRef<string | null>(null)
  const isConnectedRef = useRef(false)
  const currentCheckVersionRef = useRef(0)

  const clearAuth = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_API_KEY_KEY)
    setIsAuthenticated(false)
  }

  const logout = () => {
    clearAuth()
    wsClient.disconnect()
    setSimulationId(null)
    setStatus(null)
    setIsConnected(false)
    navigate('/')
  }

  const handleAuthSubmit = async (email: string, password: string, tier?: string) => {
    const baseUrl = import.meta.env.VITE_API_BASE || ''
    const endpoint = authMode === 'register' ? '/api/v1/auth/register' : '/api/v1/auth/login'

    try {
      setAuthLoading(true)
      setAuthError(null)

      const payload: Record<string, string> = { email, password }
      if (authMode === 'register') {
        payload.tier = tier || 'hobby'
      }

      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || t('app.auth.error.failed'))
      }

      const data = await response.json()
      if (!data.access_token) {
        throw new Error(t('app.auth.error.missingToken'))
      }

      localStorage.setItem(AUTH_TOKEN_KEY, data.access_token)
      if (data.api_key) {
        localStorage.setItem(AUTH_API_KEY_KEY, data.api_key)
      }

      setIsAuthenticated(true)
      setAuthError(null)
      navigate('/simulation', { replace: true })
    } catch (error: any) {
      setAuthError(error?.message || t('app.auth.error.failed'))
    } finally {
      setAuthLoading(false)
    }
  }

  useEffect(() => {
    if (simulationData?.step_count !== undefined) {
      console.log('âś… Frontend step update:', simulationData.step_count)
      const legendEl = document.querySelector('.legend-column .legend-info strong')
      if (legendEl) {
        legendEl.closest('.legend-column')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [simulationData?.step_count])

  const attachWsListener = (event: string, handler: Function) => {
    const handlers = wsHandlersRef.current
    const existing = handlers[event]
    if (existing) {
      wsClient.off(event, existing)
    }
    handlers[event] = handler
    wsClient.on(event, handler)
  }

  const detachAllWsListeners = () => {
    const handlers = wsHandlersRef.current
    Object.entries(handlers).forEach(([event, handler]) => {
      wsClient.off(event, handler)
    })
    wsHandlersRef.current = {}
  }

  useEffect(() => {
    simulationIdRef.current = simulationId
  }, [simulationId])

  useEffect(() => {
    isConnectedRef.current = isConnected
  }, [isConnected])

  const initializedRef = useRef(false)
  useEffect(() => {
    // NAPRAWIONE: Zawsze Ĺ‚Ä…cz siÄ™ z najnowszÄ… symulacjÄ… po odĹ›wieĹĽeniu
    // Nie blokujemy przy F5, tylko przy React StrictMode double-mount
    const wasInitialized = initializedRef.current
    initializedRef.current = true
    
    // Po odĹ›wieĹĽeniu strony (F5) - zawsze sprawdĹş current simulation
    if (!wasInitialized && isSimulationView && isAuthenticated) {
      initializeSimulation(false)
    } else {
      // JeĹ›li to React StrictMode double-mount, ignoruj
      console.log('Skipping duplicate React StrictMode mount')
    }
    
    return () => {
      // Cleanup: remove listeners and disconnect without stopping simulation
      detachAllWsListeners()
      wsClient.disconnect()
      // Reset na wypadek unmount
      initializedRef.current = false
    }
  }, [isAuthenticated, isSimulationView, wsClient])

  // Keep local simulation stable; only attach to "current" when there is no active local ID.
  useEffect(() => {
    if (!isSimulationView || !isAuthenticated) {
      return
    }

    const checkForNewSimulation = async () => {
      const version = ++currentCheckVersionRef.current
      try {
        console.log('Checking for existing simulation...')
        const currentSim = await api.getCurrentSimulation()
        if (version !== currentCheckVersionRef.current) return

        console.log('Current simulation result:', currentSim)
        if (!currentSim.exists || !currentSim.simulation_id) {
          return
        }

        const localId = simulationIdRef.current

        // Do not hijack local simulation unless local one is gone.
        if (localId && localId !== currentSim.simulation_id) {
          const localStillExists = await api.checkSimulationExists(localId)
          if (version !== currentCheckVersionRef.current) return
          if (localStillExists) {
            return
          }
        }

        const candidateExists = await api.checkSimulationExists(currentSim.simulation_id)
        if (version !== currentCheckVersionRef.current) return
        if (!candidateExists) {
          // Ignore stale "current" values returned during in-flight create/cleanup races.
          return
        }

        if (!localId || localId !== currentSim.simulation_id) {
          console.log(`Auto-attaching to simulation: ${currentSim.simulation_id}`)
          setSimulationId(currentSim.simulation_id)
          if (!isConnectedRef.current || localId !== currentSim.simulation_id) {
            await connectWebSocket(currentSim.simulation_id)
          }
          setRuntimeAccumulatedMs(0)

          const simStatus = await api.getSimulationStatus(currentSim.simulation_id)
          if (version !== currentCheckVersionRef.current) return
          if (simStatus.is_running && !simStatus.is_paused) {
            setRuntimeStartMs(Date.now())
          } else {
            setRuntimeStartMs(null)
          }
        }
      } catch (error) {
        if (version === currentCheckVersionRef.current && isSimulationView) {
          console.error('Failed to check for new simulation:', error)
        }
      }
    }

    checkForNewSimulation()
    const interval = setInterval(checkForNewSimulation, 30000)
    return () => {
      currentCheckVersionRef.current += 1
      clearInterval(interval)
    }
  }, [api, isAuthenticated, isSimulationView])

  // Poll status periodically so the time advances even if WS data is delayed
  useEffect(() => {
    if (!simulationId || !isAuthenticated || !isSimulationView) return
    const interval = setInterval(() => {
      updateStatus(simulationId)
    }, 20000)  // Poll status every 20s; WS provides live data
    return () => clearInterval(interval)
  }, [simulationId, api, isAuthenticated, isSimulationView])

  // Runtime clock tick
  useEffect(() => {
    const tick = setInterval(() => setRuntimeNowMs(Date.now()), 200)
    return () => clearInterval(tick)
  }, [])

  // Recreate simulation when mode changes
  useEffect(() => {
    if (!initializedRef.current || !isAuthenticated || !isSimulationView) return
    if (!modeInitializedRef.current) {
      modeInitializedRef.current = true
      return
    }
    const activeSimulationId = simulationIdRef.current
    if (!activeSimulationId) return
    const recreate = async () => {
      try {
        // Disconnect current WS
        wsClient.disconnect()
        setIsConnected(false)
        // Reset runtime
        setRuntimeAccumulatedMs(0)
        setRuntimeStartMs(null)
        // Create new simulation in selected mode
        const response = await api.createSimulation({
          config: {  // Note: using 'as any' below to allow extra thermodynamic validation fields
            grid_height: 128,
            grid_width: 128,
            mode,
            max_particles: 500,
            max_time: 1000,
            dt: 0.005,  // NAPRAWIONE: byĹ‚o 0.035 (7Ă— za duĹĽo!) - stabilnoĹ›Ä‡ numeryczna
            energy_decay: 0.96,  // Wolniejsze wygaszanie z 0.95
            energy_threshold: 0.1,
            // Energy system parameters - OPTIMIZED FOR MORE DYNAMICS
            pulse_every: 24,  // CzÄ™stsze pulsy (co 24 kroki)
            pulse_radius: 32.0,  // WiÄ™kszy promieĹ„ (32 jednostki)
            pulse_amplitude: 8.0,  // WiÄ™ksza amplituda (8.0)
            diffuse_D: 0.5,  // Szybsza dyfuzja energii
            target_energy: 0.5,  // WyĹĽsze tĹ‚o energii
            thermostat_alpha: 0.01,  // Silniejszy termostat
            // Thermodynamic validation - SMART VALIDATION (GROMACS/NAMD best practices)
            // Energy+Momentum: every call (~2ms), M-B: every 20k steps, Entropy: every 50k steps
            enable_thermodynamic_validation: true,  // WĹ‚Ä…czone - smart validation (5,650Ă— szybciej!)
            validate_every_n_steps: 1000,  // CzÄ™Ĺ›ciej ale z szybkimi testami (Energy+Momentum)
            // Performance optimization parameters
            energy_update_interval: 5,  // Update energy every 5 steps
            metrics_update_interval: 10,  // Update metrics every 10 steps
            diagnostics_frequency: 20,  // Log diagnostics every 20 steps
            // Particle and binding parameters
            particle_radius: 0.5,
            binding_threshold: 0.25,  // NAPRAWIONE: ObniĹĽone z 0.68 dla Ĺ‚atwiejszego tworzenia wiÄ…zaĹ„
            unbinding_threshold: 0.15,  // NAPRAWIONE: ObniĹĽone z 0.3 dla stabilniejszych wiÄ…zaĹ„
            // Mutation parameters - OPTIMIZED FOR MORE NOVELTY
            p_mut_base: 5e-4,  // ZwiÄ™kszone mutacje (5x wiÄ™cej)
            p_mut_gain: 20.0,  // ZwiÄ™kszone z 14.0
            attr_sigma: 0.12,  // Silniejsza perturbacja
            // Open chemistry parameters
            vmax: 12.0,  // ZwiÄ™kszona prÄ™dkoĹ›Ä‡ maksymalna
            neighbor_radius: 4.0,  // WiÄ™kszy promieĹ„ sÄ…siedztwa
            rebuild_neighbors_every: 6,  // CzÄ™stsza przebudowa sÄ…siadĂłw
            clamp_density_per_cell: 64,  // NOWE: maksymalna gÄ™stoĹ›Ä‡ na komĂłrkÄ™
            // Other parameters
            novelty_window: 100,
            min_cluster_size: 2,
            vis_frequency: 5,  // Rzadsze renderowanie
            log_frequency: 100,
            seed: Math.floor(Math.random() * 1000000)
          } as any,  // Allow extra config fields for backend (Pydantic accepts additional fields)
          mode
        })
        if (response.success && response.simulation_id) {
          setSimulationId(response.simulation_id)
          await connectWebSocket(response.simulation_id)
          // Auto-start simulation after creation
          setRuntimeAccumulatedMs(0)
          await startSimulation(response.simulation_id)
          // startSimulation already sets runtimeStartMs to Date.now()
        }
      } catch (err) {
        console.error('Failed to recreate simulation:', err)
      }
    }
    recreate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSimulationView, mode])

  const initializeSimulation = async (createIfMissing = false) => {
    if (!isSimulationView || !isAuthenticated) {
      return
    }
    
    try {
      // Check if simulation already exists
      const currentSim = await api.getCurrentSimulation()
      if (currentSim.exists && currentSim.simulation_id) {
        console.log('Using existing simulation:', currentSim.simulation_id)
        setSimulationId(currentSim.simulation_id)
        await connectWebSocket(currentSim.simulation_id)
        setRuntimeAccumulatedMs(0)
        setRuntimeStartMs(null)
        return
      }

      if (!createIfMissing) {
        return
      }

      // Create new simulation only on explicit user action
      const response = await api.createSimulation({
        config: {
          grid_height: 128,
          grid_width: 128,
          mode,
          max_particles: 500,
          max_time: 1000,
          dt: 0.005,  // NAPRAWIONE: byĹ‚o 0.035 (7Ă— za duĹĽo!) - stabilnoĹ›Ä‡ numeryczna
          energy_decay: 0.96,  // Wolniejsze wygaszanie z 0.99
          energy_threshold: 0.01,
          pulse_every: 24,  // CzÄ™stsze pulsy (co 24 kroki)
          pulse_radius: 32.0,  // WiÄ™kszy promieĹ„ (32 jednostki)
          pulse_amplitude: 8.0,  // WiÄ™ksza amplituda (8.0)
          diffuse_D: 0.5,  // Szybsza dyfuzja energii
          target_energy: 0.5,  // WyĹĽsze tĹ‚o energii
          thermostat_alpha: 0.01,  // Silniejszy termostat
          // Performance optimization parameters
          energy_update_interval: 5,  // Update energy every 5 steps
          metrics_update_interval: 10,  // Update metrics every 10 steps
          diagnostics_frequency: 20,  // Log diagnostics every 20 steps
          particle_radius: 0.5,
          binding_threshold: 0.25,  // NAPRAWIONE: ObniĹĽone z 0.68 dla Ĺ‚atwiejszego tworzenia wiÄ…zaĹ„
          unbinding_threshold: 0.15,  // NAPRAWIONE: ObniĹĽone z 0.3 dla stabilniejszych wiÄ…zaĹ„
          novelty_window: 100,
          min_cluster_size: 2,
          vis_frequency: 5,  // Rzadsze renderowanie
          log_frequency: 100,
          p_mut_base: 5e-4,  // ZwiÄ™kszone mutacje (5x wiÄ™cej)
          p_mut_gain: 20.0,  // ZwiÄ™kszone z 14.0
          attr_sigma: 0.12,  // Silniejsza perturbacja
          // Open chemistry parameters
          vmax: 12.0,  // ZwiÄ™kszona prÄ™dkoĹ›Ä‡ maksymalna
          neighbor_radius: 3.2,  // NOWE: promieĹ„ sÄ…siedztwa
          rebuild_neighbors_every: 8,  // NOWE: czÄ™stotliwoĹ›Ä‡ odbudowy sÄ…siedztwa
          clamp_density_per_cell: 64,  // NOWE: maksymalna gÄ™stoĹ›Ä‡ na komĂłrkÄ™
          seed: Math.floor(Math.random() * 1000000)
        },
        mode
      })

      if (response.success && response.simulation_id) {
        setSimulationId(response.simulation_id)
        await connectWebSocket(response.simulation_id)
        setRuntimeAccumulatedMs(0)
        await startSimulation(response.simulation_id)
      }
    } catch (error) {
      console.error('Failed to initialize simulation:', error)
    }
  }

  const connectWebSocket = async (id: string | null) => {
    try {
      if (!id) {
        console.error('connectWebSocket called without simulationId')
        return
      }
      
      // Check if simulation exists before connecting
      const exists = await api.checkSimulationExists(id)
      if (!exists) {
        // Avoid aggressive local state resets on transient races during create/switch.
        console.warn(`Simulation ${id} not found on backend. Skipping WebSocket connect.`)
        return
      }
      
      // Pass only the simulationId; ws client will build the full URL
      detachAllWsListeners()
      wsClient.disconnect()
      await wsClient.connect(id)
      setIsConnected(true)
      
      const handleSimulationData = (data: SimulationData) => {
        setSimulationData((prev: SimulationData | null) => {
          if (!prev || prev.step_count !== data.step_count) {
            console.log('đźŽŻ Updating simulationData to step', data.step_count)
          }
          return data
        })
        if (data.metrics) {
          setMetricsSnapshot(data.metrics)
        }
        
        // Update performance data
        if (data.performance) {
          setPerformanceData(data.performance)
        }
        
        // Extract available species for preset mode
        if (data.concentrations && Object.keys(data.concentrations).length > 0) {
          setAvailableSpecies(Object.keys(data.concentrations))
        }
      }

      const handleDisconnected = () => {
        setIsConnected(false)
      }

      const handleError = (error: Error) => {
        console.error('WebSocket error:', error)
        setIsConnected(false)
      }

      const handleSimulationNotFound = () => {
        console.warn('Simulation not found on backend. Clearing simulation ID.')
        setIsConnected(false)
        setSimulationId(null)
        setStatus(null)
        // Don't create new simulation automatically
      }

      const handleReconnectFailed = () => {
        console.error('Failed to reconnect after multiple attempts')
        setIsConnected(false)
      }

      attachWsListener('simulation_data', handleSimulationData)
      attachWsListener('disconnected', handleDisconnected)
      attachWsListener('error', handleError)
      attachWsListener('simulation_not_found', handleSimulationNotFound)
      attachWsListener('reconnect_failed', handleReconnectFailed)
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
    }
  }

  const startSimulation = async (id: string) => {
    try {
      let targetId = id
      const exists = await api.checkSimulationExists(targetId)
      if (!exists) {
        const currentSim = await api.getCurrentSimulation()
        if (currentSim.exists && currentSim.simulation_id) {
          targetId = currentSim.simulation_id
          if (targetId !== simulationIdRef.current) {
            setSimulationId(targetId)
          }
        }
      }

      await api.startSimulation(targetId)
      await updateStatus(targetId)
      await refreshMetrics(targetId)
      // start wall-clock runtime
      setRuntimeStartMs(Date.now())
    } catch (error) {
      console.error('Failed to start simulation:', error)
    }
  }

  const pauseSimulation = async () => {
    if (!simulationId) return
    
    try {
      await api.pauseSimulation(simulationId)
      await updateStatus(simulationId)
      await refreshMetrics(simulationId)
      // accumulate runtime and stop clock
      if (runtimeStartMs !== null) {
        setRuntimeAccumulatedMs(prev => prev + (Date.now() - runtimeStartMs))
        setRuntimeStartMs(null)
      }
    } catch (error) {
      console.error('Failed to pause simulation:', error)
    }
  }

  const resumeSimulation = async () => {
    if (!simulationId) return
    
    try {
      await api.resumeSimulation(simulationId)
      await updateStatus(simulationId)
      // resume wall-clock
      if (runtimeStartMs === null) {
        setRuntimeStartMs(Date.now())
      }
    } catch (error) {
      console.error('Failed to resume simulation:', error)
    }
  }

  const stopSimulation = async () => {
    if (!simulationId) return
    
    try {
      await api.stopSimulation(simulationId)
      wsClient.disconnect()
      setIsConnected(false)
      await updateStatus(simulationId)
      // stop wall-clock without resetting accumulated (keeps total)
      if (runtimeStartMs !== null) {
        setRuntimeAccumulatedMs(prev => prev + (Date.now() - runtimeStartMs))
        setRuntimeStartMs(null)
      }
    } catch (error) {
      console.error('Failed to stop simulation:', error)
    }
  }

  const resetSimulation = async () => {
    if (!simulationId) return
    
    try {
      await api.resetSimulation(simulationId)
      await updateStatus(simulationId)
      // reset wall-clock
      setRuntimeAccumulatedMs(0)
      setRuntimeStartMs(Date.now())
    } catch (error) {
      console.error('Failed to reset simulation:', error)
    }
  }

  const updateStatus = async (id: string) => {
    try {
      const statusData = await api.getSimulationStatus(id)
      setStatus(statusData)
    } catch (error: any) {
      // If simulation not found (404), clear simulation ID to prevent repeated errors
      if (error?.message?.includes('404') || error?.message?.includes('not found')) {
        console.warn(`Simulation ${id} no longer exists. Clearing simulation ID.`)
        setSimulationId(null)
        setStatus(null)
      } else {
        console.error('Failed to update status:', error)
      }
    }
  }

  const refreshMetrics = async (id: string) => {
    try {
      const response = await api.getMetrics(id)
      setMetricsSnapshot(response.metrics)
    } catch (error) {
      console.error('Failed to refresh metrics:', error)
    }
  }

  const getStatusIndicator = () => {
    if (!status && !simulationData) return 'disconnected'
    const running = status?.is_running ?? true
    const paused = status?.is_paused ?? false
    if (running && !paused) return 'running'
    if (paused) return 'paused'
    return 'stopped'
  }

  const getStatusText = () => {
    if (!status && !simulationData) return t('app.status.disconnected')
    const running = status?.is_running ?? true
    const paused = status?.is_paused ?? false
    if (running && !paused) return t('app.status.running')
    if (paused) return t('app.status.paused')
    return t('app.status.stopped')
  }

  const apiBaseUrl = import.meta.env.VITE_API_BASE || (import.meta.env.DEV ? 'http://localhost:8001' : '')
  const storedApiKey = typeof window !== 'undefined' ? (localStorage.getItem(AUTH_API_KEY_KEY) || undefined) : undefined

  return (
    <div className="app">
      {isAuthenticated && isAppView && (
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src={logoImage} alt={t('app.logo.alt')} style={{ height: '160px', width: 'auto' }} />
          <div className="status">
            <div className={`status-indicator ${getStatusIndicator()}`} />
            <span>{getStatusText()}</span>
            {simulationId && (
              <span className="text-sm text-gray-400 ml-2">
                {t('app.status.id')} {simulationId.slice(-8)}
              </span>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <img src={headerImage} alt={t('app.header.alt')} style={{ height: '160px', width: 'auto' }} />
        </div>
        
        <div className="flex items-center gap-2">
          <Link
            to={view === 'api-v1' ? '/simulation' : '/api-v1'}
            className={`btn ${view === 'api-v1' ? 'btn-primary' : 'btn-secondary'}`}
            title={t('app.nav.toggleApi.title')}
          >
            <Database size={16} />
            {view === 'api-v1' ? t('app.nav.simulation') : t('app.nav.apiJobs')}
          </Link>
          
          <Link
            to="/swarm"
            className={`btn ${view === 'swarm' ? 'btn-primary' : 'btn-secondary'}`}
            title={t('app.nav.swarm.title')}
          >
            {t('app.nav.swarm')}
          </Link>
          
          {isSimulationView && (
            <>
              <label htmlFor="mode-select" className="text-sm text-gray-300">{t('app.mode.label')}</label>
              <select
                id="mode-select"
                value={mode}
                onChange={(e) => setMode(e.target.value as any)}
                className="text-sm px-2 py-1 border border-white/20 rounded bg-white/10 text-white"
                aria-label={t('app.mode.aria')}
              >
                <option value="open_chemistry">{t('legend.mode.open')}</option>
                <option value="preset_prebiotic">{t('legend.mode.preset')}</option>
              </select>
              
              <button
                className="btn btn-primary"
                onClick={status?.is_running ? (status?.is_paused ? resumeSimulation : pauseSimulation) : (simulationId ? () => startSimulation(simulationId) : () => initializeSimulation(true))}
                disabled={false}
              >
                {status?.is_running ? (status?.is_paused ? <Play size={16} /> : <Pause size={16} />) : <Play size={16} />}
                {status?.is_running ? (status?.is_paused ? t('app.action.resume') : t('app.action.pause')) : (simulationId ? t('app.action.start') : t('app.action.createStart'))}
              </button>
              
              <button
                className="btn btn-danger"
                onClick={stopSimulation}
                disabled={!simulationId}
              >
                <Square size={16} />
                {t('app.action.stop')}
              </button>
              
              <button
                className="btn btn-secondary"
                onClick={resetSimulation}
                disabled={!simulationId}
              >
                <RotateCcw size={16} />
                {t('app.action.reset')}
              </button>
            </>
          )}

          <LanguageSwitcher />

          <button className="btn btn-secondary" onClick={logout}>
            {t('app.action.logout')}
          </button>
        </div>
      </header>
      )}

      <Routes>
        <Route
          path="/"
          element={
            <LandingPage
              isAuthenticated={isAuthenticated}
              logoSrc={logoImage}
              heroImageSrc={headerImage}
            />
          }
        />

        <Route path="/login" element={
          isAuthenticated ? (
            <Navigate to="/simulation" replace />
          ) : (
            <main className="main-content" style={{ padding: '2rem', position: 'relative' }}>
              <div className="lp-lang">
                <LanguageSwitcher />
              </div>
              <Auth
                mode={authMode}
                onModeChange={setAuthMode}
                onSubmit={handleAuthSubmit}
                onCancel={() => navigate('/')}
                loading={authLoading}
                error={authError}
              />
            </main>
          )
        } />

        <Route path="/api-v1" element={
          isAuthenticated ? (
            <main className="main-content" style={{ padding: '2rem' }}>
              <APIv1Jobs apiBaseUrl={apiBaseUrl} apiKey={storedApiKey} />
            </main>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
        <Route path="/swarm" element={
          isAuthenticated ? (
            <main className="main-content" style={{ padding: '2rem' }}>
              <SwarmDashboard apiBaseUrl={apiBaseUrl} />
            </main>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
        <Route path="/simulation" element={
        isAuthenticated ? (
        <>
          <main className="main-content">
            <aside className="sidebar">
              <Controls
                simulationId={simulationId}
                status={status}
                onStatusUpdate={updateStatus}
                availableSpecies={availableSpecies}
                selectedSubstance={selectedSubstance}
                onSubstanceChange={setSelectedSubstance}
                runtimeMs={runtimeAccumulatedMs + (runtimeStartMs ? (runtimeNowMs - runtimeStartMs) : 0)}
              />
              
              <PerformancePanel
                simulationId={simulationId}
                performanceData={performanceData}
              />
              
              <GraphPreview
                data={simulationData}
                selectedSubstance={selectedSubstance}
                onSubstanceSelect={setSelectedSubstance}
              />
              
              {/* Novelty Panel with PubChem Matcher */}
              {simulationId && (
                <div className="mt-6">
                  <NoveltyPanel
                    simulationId={simulationId}
                    onSubstanceSelect={setSelectedSubstance}
                  />
                </div>
              )}
            </aside>

            <div className="canvas-container">
              <HeatmapCanvas
                data={simulationData}
                selectedSubstance={selectedSubstance}
                isConnected={isConnected}
              />
            </div>
          </main>
          
          {/* Legend at bottom */}
          <footer className="legend-footer" key={`legend-${simulationData?.step_count ?? status?.step_count ?? 0}`}>
            <div className="legend">
          <h3>{t('legend.title')}</h3>
          <div className="legend-grid">
            <div className="legend-column">
              <h4>{t('legend.viz.title')}</h4>
              <div className="legend-item">
                <div className="legend-dot" style={{ background: '#60a5fa' }}></div>
                <span>{t('legend.viz.particles')}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ background: '#fbbf24' }}></div>
                <span>{t('legend.viz.energy')}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ background: '#34d399' }}></div>
                <span>{t('legend.viz.bonds')}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot" style={{ background: '#f87171' }}></div>
                <span>{t('legend.viz.highEnergy')}</span>
              </div>
            </div>
            <div className="legend-column" key={`status-${simulationData?.step_count ?? status?.step_count ?? 0}`}>
              <h4>{t('legend.status.title')}</h4>
              <div className="legend-info">
                <strong>{t('legend.status.mode')}:</strong>{' '}
                {mode === 'open_chemistry' ? t('legend.mode.open') : t('legend.mode.preset')}
              </div>
              <div className="legend-info" key={`step-${simulationData?.step_count ?? status?.step_count ?? 0}`}>
                <strong>{t('legend.status.step')}:</strong> {simulationData?.step_count ?? status?.step_count ?? 0}
              </div>
              <div className="legend-info" key={`time-${simulationData?.current_time ?? 0}`}>
                <strong>{t('legend.status.time')}:</strong> {simulationData?.current_time?.toFixed(2) ?? '0.00'}s
              </div>
              <div className="legend-info">
                <strong>{t('legend.status.particles')}:</strong> {metricsSnapshot?.particle_count ?? status?.particle_count ?? 0}
              </div>
            </div>
            <div className="legend-column">
              <h4>{t('legend.watch.title')}</h4>
              <div className="legend-info">
                • <strong>{t('legend.watch.bonds')}</strong> — {t('legend.watch.bonds.desc')}
              </div>
              <div className="legend-info">
                • <strong>{t('legend.watch.pulses')}</strong> — {t('legend.watch.pulses.desc')}
              </div>
              <div className="legend-info">
                • <strong>{t('legend.watch.selfOrg')}</strong> — {t('legend.watch.selfOrg.desc')}
              </div>
              <div className="legend-info">
                • <strong>{t('legend.watch.novelty')}</strong> — {t('legend.watch.novelty.desc')}
              </div>
            </div>
          </div>
        </div>
        </footer>
        </>
        ) : (
          <Navigate to="/login" replace />
        )
        } />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  )
}

export default App

